import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../store';
import { fetchTrash, restoreFromTrash, permanentDeleteFromTrash, emptyTrash } from '../../store/actions/FolderAction';
import { showModal, hideModal } from '../../store/actions/modalActions';
import Table from '../shared/TailwindComponents/Table';
import ListCountBadge from '../shared/TailwindComponents/ListCountBadge';
import Badge from '../shared/TailwindComponents/Badge';
import { FileGlyph, FolderGlyph } from './fileGlyphs';
import {
  PiTrashLight,
  PiArrowCounterClockwiseLight,
  PiRecycleLight,
  PiRecycleFill,
  PiWarningCircleLight,
} from 'react-icons/pi';
import { Tooltip } from '@mui/material';
import { formatDateTime } from '../../utils/functions';
import RecycleBinDeleteConfirmation from '../RecycleBin/RecycleBinDeleteConfirmation';

interface TrashItem {
  FolderID?: string;
  FileID?: string;
  FolderName?: string;
  FileName?: string;
  Size?: number;
  MimeType?: string;
  DeletedAt: string;
  DeletedByUserID?: number;
  DeletedByUserUUID?: string;
  OriginalParentID?: string;
  OriginalFolderID?: string;
  OriginalLocation?: string;
  DaysInTrash: number;
  DaysRemaining: number;
  Type: 'folder' | 'file';
  Tags?: Array<{ id: number; uuid: string; name: string; color: string }>;
}

interface TrashProps {
  onClose?: () => void;
  emptyTrashRef?: React.MutableRefObject<(() => void) | null>;
  onBulkActionsChange?: (actions: { label: string; onClick: () => void; icon?: React.ReactElement }[]) => void;
  onSelectedCountChange?: (count: number) => void;
}

const Trash: React.FC<TrashProps> = ({ onClose, emptyTrashRef, onBulkActionsChange, onSelectedCountChange }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch<AppDispatch>();
  const userInfo = useSelector((state: RootState) => state.user.userInfo);
  const isAdmin = !!userInfo?.is_org_admin;

  const [items, setItems] = useState<TrashItem[]>([]);
  const [selectedItems, setSelectedItems] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  // Appending a page is a different wait from the first load: one belongs at
  // the bottom of the existing rows, the other replaces them. Sharing a single
  // flag meant the first load showed the load-more skeleton under an empty
  // table instead of the table's own.
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [retentionDays, setRetentionDays] = useState(30);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('deleted_at');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 50,
    total_items: 0,
    total_pages: 1,
    has_next: false,
    has_previous: false
  });

  const loadTrash = useCallback(async (page = 1, append = false) => {
    if (append) setIsLoadingMore(true); else setIsLoading(true);
    try {
      const result = await dispatch(fetchTrash({ page, limit: 50, sortBy, sortOrder, searchTerm }) as any);
      if (result?.success) {
        // Use 'items' (sorted interleaved list) if available, fall back to combining folders+files
        const pageItems: TrashItem[] = result.items || [
          ...(result.folders || []),
          ...(result.files || [])
        ];
        setItems(prev => append ? [...prev, ...pageItems] : pageItems);
        setRetentionDays(result.retention_days || 30);
        if (result.pagination) {
          setPagination(result.pagination);
        }
      }
    } finally {
      if (append) setIsLoadingMore(false); else setIsLoading(false);
    }
  }, [dispatch, sortBy, sortOrder, searchTerm]);

  useEffect(() => {
    loadTrash();
  }, [loadTrash]);

  const getItemId = (item: TrashItem): string => {
    return item.FolderID || item.FileID || '';
  };

  const getItemName = (item: TrashItem): string => {
    return item.FolderName || item.FileName || 'Untitled';
  };

  const handleRestore = async (itemsToRestore?: TrashItem[]) => {
    const targets = itemsToRestore || items.filter(i => selectedItems.includes(getItemId(i)));
    const folderIds = targets.filter(i => i.Type === 'folder').map(i => i.FolderID!);
    const fileIds = targets.filter(i => i.Type === 'file').map(i => i.FileID!);

    setIsLoading(true);
    try {
      const success = await dispatch(restoreFromTrash(folderIds, fileIds) as any);
      if (success) {
        setSelectedItems([]);
        await loadTrash(1);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handlePermanentDelete = async (itemsToDelete?: TrashItem[]) => {
    const targets = itemsToDelete || items.filter(i => selectedItems.includes(getItemId(i)));
    const folderIds = targets.filter(i => i.Type === 'folder').map(i => i.FolderID!);
    const fileIds = targets.filter(i => i.Type === 'file').map(i => i.FileID!);

    dispatch(
      showModal({
        type: 'custom',
        message: t('Permanently Delete'),
        icon: <PiTrashLight className="w-6 h-6 text-danger-600" />,
        content: (
          <RecycleBinDeleteConfirmation
            itemCount={targets.length}
            onConfirm={async () => {
              setIsLoading(true);
              try {
                const success = await dispatch(permanentDeleteFromTrash(folderIds, fileIds) as any);
                if (success) {
                  setSelectedItems([]);
                  await loadTrash(1);
                }
              } finally {
                setIsLoading(false);
                dispatch(hideModal());
              }
            }}
            onCancel={() => dispatch(hideModal())}
          />
        ),
        onCancel: () => {},
        showModal: true
      })
    );
  };

  const handleEmptyTrash = () => {
    dispatch(
      showModal({
        type: 'custom',
        message: t('Empty Trash'),
        icon: <PiRecycleFill className="w-6 h-6 text-danger-600" />,
        content: (
          <RecycleBinDeleteConfirmation
            itemCount={pagination.total_items}
            isEmptyAll
            onConfirm={async () => {
              setIsLoading(true);
              try {
                const success = await dispatch(emptyTrash() as any);
                if (success) {
                  setItems([]);
                  setSelectedItems([]);
                }
              } finally {
                setIsLoading(false);
                dispatch(hideModal());
              }
            }}
            onCancel={() => dispatch(hideModal())}
          />
        ),
        onCancel: () => {},
        showModal: true
      })
    );
  };

  // Expose handleEmptyTrash to parent via ref
  if (emptyTrashRef) {
    emptyTrashRef.current = handleEmptyTrash;
  }

  const formatDate = (dateStr: string): string => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Search is handled server-side (matches name + tag names across full dataset)
  const filteredItems = items;

  const expiringItems = items.filter(i => i.DaysRemaining <= 7 && i.DaysRemaining > 0);

  // Build table data matching Files.tsx pattern
  const columns = ['Select', 'Name', 'Type', 'Tags', 'Original Location', 'Deleted', 'Days Remaining', 'Actions'];

  const tableData = filteredItems.map((item) => ({
    id: getItemId(item),
    doc: item,
    Select: null,
    Name: (
      <div className="flex items-center gap-2 min-w-0">
        {item.Type === 'folder' ? (
          <FolderGlyph folderName={item.FolderName} className="w-5 h-5 flex-shrink-0" />
        ) : (
          <FileGlyph fileName={item.FileName || ''} mimeType={item.MimeType} className="w-5 h-5 flex-shrink-0" />
        )}
        <span className="truncate text-fg-strong" title={getItemName(item)}>
          {getItemName(item)}
        </span>
      </div>
    ),
    Type: item.Type === 'folder'
      ? <Badge color="blue" className="text-xs whitespace-nowrap">{t('Folder')}</Badge>
      : <Badge color="gray" className="text-xs whitespace-nowrap">{t('File')}</Badge>,
    Tags: (
      <div className="flex flex-wrap gap-1">
        {(item.Tags || []).map((tag) => (
          <Badge key={tag.uuid} color={tag.color as any} className="text-xs whitespace-nowrap">
            {tag.name}
          </Badge>
        ))}
      </div>
    ),
    'Original Location': (
      <Tooltip title={item.OriginalLocation || t('Root')} arrow placement="top">
        <span className="text-sm text-fg-muted truncate max-w-[200px] inline-block cursor-default">
          {item.OriginalLocation || t('Root')}
        </span>
      </Tooltip>
    ),
    Deleted: formatDateTime(item.DeletedAt),
    'Days Remaining': (
      <span className={`text-sm font-medium ${item.DaysRemaining <= 7 ? 'text-danger-600 dark:text-danger-400' : 'text-fg-muted'}`}>
        {item.DaysRemaining} {t('days')}
      </span>
    ),
    isFolder: item.Type === 'folder',
  }));

  const tableActions = [
    {
      label: t('Restore'),
      onClick: (row: any) => handleRestore([row.doc]),
      icon: <PiArrowCounterClockwiseLight className="h-4 w-4" />,
    },
    {
      label: t('Delete Permanently'),
      onClick: (row: any) => handlePermanentDelete([row.doc]),
      icon: <PiTrashLight className="h-4 w-4" />,
      isDestructive: true,
    },
  ];

  const handleSelectItem = (id: string) => {
    setSelectedItems(prev => prev.includes(id) ? prev.filter(item => item !== id) : [...prev, id]);
  };

  const selectableIds = tableData.map(row => row.id);
  const handleSelectAllItems = () => {
    setSelectedItems(selectedItems.length === selectableIds.length ? [] : selectableIds);
  };

  const handleLoadMore = () => {
    if (pagination.has_next) {
      loadTrash(pagination.page + 1, true);
    }
  };

  // Keep refs to latest handlers so parent's onClick closures are never stale
  const restoreRef = useRef(() => handleRestore());
  const deleteRef = useRef(() => handlePermanentDelete());
  restoreRef.current = () => handleRestore();
  deleteRef.current = () => handlePermanentDelete();

  useEffect(() => {
    if (!onBulkActionsChange) return;
    if (selectedItems.length > 0) {
      onBulkActionsChange([
        { label: t('Restore Selected'), onClick: () => restoreRef.current(), icon: <PiArrowCounterClockwiseLight className="h-4 w-4" /> },
        { label: t('Delete Selected'), onClick: () => deleteRef.current(), icon: <PiTrashLight className="h-4 w-4" /> },
      ]);
    } else {
      onBulkActionsChange([]);
    }
  }, [selectedItems, onBulkActionsChange, t]);

  useEffect(() => {
    onSelectedCountChange?.(selectedItems.length);
  }, [selectedItems.length, onSelectedCountChange]);

  return (
    <>
      {/* A quiet line, not a panel: a warning-toned icon and muted text under
          the toolbar. One sentence never needed a filled container. */}
      {expiringItems.length > 0 && (
        <div className="mx-4 mb-3 flex-shrink-0 flex items-center gap-2 text-sm text-fg-muted">
          <PiWarningCircleLight className="w-4 h-4 flex-shrink-0 text-warning-500" />
          <span>
            {expiringItems.length} {expiringItems.length === 1 ? t('item') : t('items')} {t('will be permanently deleted within 7 days')}
          </span>
        </div>
      )}

      {/* ONE scroller, the Table's. This was an `overflow-auto` box around an
          `overflow-x-auto overflow-y-auto` box around a Table that scrolls its
          own rows — so the bin scrolled its own box inside a second scrollbar,
          and the horizontal one ended up under the last row instead of at the
          bottom of the view. */}
      <div className="flex-1 min-h-0 flex flex-col relative">
          <div className="flex-1 min-h-0 flex flex-col">
            {/* No filter bar in this view — see ListCountBadge. */}
            <ListCountBadge
              showing={tableData.length}
              total={pagination.total_items}
              label="items"
            />
            <Table
              isLoading={isLoading}
              isLoadingMore={isLoadingMore}
              columns={columns}
              data={tableData}
              actions={tableActions}
              enableSelection
              selectedIds={selectedItems}
              onSelect={handleSelectItem}
              onSelectAll={handleSelectAllItems}
              hasMore={pagination.has_next}
              onLoadMore={handleLoadMore}
              resizableColumns={true}
              defaultColumnWidths={{
                'Select': 48,
                'Name': 250,
                'Type': 100,
                'Tags': 160,
                'Original Location': 200,
                'Deleted': 160,
                'Days Remaining': 130,
                'Actions': 48,
              }}
              sortableColumns={['Name', 'Deleted', 'Days Remaining']}
              sortBy={sortBy === 'deleted_at' ? 'deleted' : sortBy === 'days_remaining' ? 'days_remaining' : sortBy}
              sortOrder={sortOrder}
              onSort={(col, order) => {
                const columnMap: Record<string, string> = {
                  'name': 'name',
                  'deleted': 'deleted_at',
                  'days_remaining': 'days_remaining',
                };
                setSortBy(columnMap[col] || col);
                setSortOrder(order);
              }}
              total_items={pagination.total_items}
              total_showing_items={tableData.length}
              max={expiringItems.length > 0 ? 'max-h-[calc(100vh-175px)]' : 'max-h-[calc(100vh-135px)]'}
              min={expiringItems.length > 0 ? 'max-h-[calc(100vh-175px)]' : 'max-h-[calc(100vh-135px)]'}
              setIsShowing={true}
              showingLabel={t('items')}
              infoText={`${t('Items in the trash are automatically deleted after')} ${retentionDays} ${t('days.')}${isAdmin ? ` ${t('You can change this in Organization Settings.')}` : ''}`}
              emptyState={{
                icon: <PiRecycleLight />,
                headline: t('Trash is empty'),
                subtext: t('Deleted files and folders will appear here'),
              }}
            />
          </div>
      </div>
    </>
  );
};

export default Trash;
